/**
 * Fixed ids for the route examples (1.0b deliverable 9), set in the demo campaign "The Drowned
 * Lanterns". Every id in an example request is one of these values, so an example succeeds on a
 * database seeded with its gate's demo layer (`demo@p1a` for `editor-alpha` and `exit-1a`,
 * `demo@p1b` for `exit-1b`) once that layer holds a row at each id in the stated state. 1.0a's
 * layers adopt the ids (its stage 1c for `p1a`, its stage 2b for `p1b`): the named demo entity
 * takes the id, and where the layer has no such entity it adds a small synthetic one.
 *
 * The set is complete for the routes of deliverables 7, 14, 17 and 19 (stages 1b, 2, 2b and 2c),
 * so the layers can be written before the routes that use them. A later stage that needs another
 * id adds it here and asks the layer's owner for the row in the same wave.
 *
 * Each id is a UUIDv7 whose time field is `EXAMPLE_NOW` (2026-09-12T19:00:00.000Z, the
 * testkit's `TEST_EPOCH`). The last group is `0000000e` and a four-digit counter, a range the
 * demo ids (`0000000d` and a counter) never use; the counter's first two digits group the kinds
 * (`00` documents, `01` blocks, `02` types, and so on).
 *
 * An entry whose condition is `absent` is named only by an example that declares a success skip,
 * or only by an example answer (the row a create would make): no layer writes it.
 *
 * An entry with `srd` is a row of 1.AA's SRD library (CS r3a deliverable 84; DECISIONS 214 ruling
 * 26): its id is 1.AA's (`srdDocumentId`, `srdSheetId`: a UUIDv5 under the library's namespace),
 * written here as a literal, and the `srd-5.1` entry with `srd.upstreamKey` holds it (a sheet
 * entry: that entry's primary sheet). The seed puts the library in the `empty` and `demo` templates,
 * under every layer; `layer` names the layer whose examples need it. The UUIDv7 and range rules
 * above hold for every entry without `srd`.
 */
/** The kinds of row an example id names. */
export const EXAMPLE_ID_KINDS = [
    'document',
    'block',
    'doc_type',
    'field_def',
    'template',
    'link',
    'sidebar_item',
    'asset',
    'export',
    'calendar',
    'placement',
    'constraint',
    'clock_advance',
    'entity_state',
    'knowledge_event',
    'actor_sheet',
    'quarantine_entry',
    // CS r4a: an `import_run` row, written by `demo@p2a` (CS r4b).
    'import_run',
    // CS r5a: a run-sheet item and a session capture, written by `demo@p2b` (CS r5b).
    'session_item',
    'capture',
    // CS r4b: a `saved_view` row, written by `demo@p2a`.
    'saved_view',
    // CS r5b: a session's source file and a change batch, written by `demo@p2b`.
    'session_source',
    'change_batch',
    // CS r6a: a Foundry import's row, written by `demo@p2c` (CS r6b).
    'actor_import',
];
/**
 * The demo layer that writes the row. `p2a` is CS r4b's (the import runs and the saved views); an
 * example naming a `p2a` id declared the success skip `SEEDED_BY_P2A` until r4b wrote the layer
 * (CS r4a deliverable 112, r4b deliverable 124). Six `p2a` examples skip their success for another
 * reason (DECISIONS 492): `import.runs.uploadPart`, `.complete`, `.plan`, `.putMapping`, `.dryRun`
 * and `import.pages.get` need the upload's or the plan's file under the data directory, which a
 * database seed does not hold.
 * `p2b` is CS r5b's (session 3's run sheet and the session captures); until it exists, an example
 * naming a `p2b` id declares `SEEDED_BY_P2B` (CS r5a deliverable 151).
 * `p2c` is CS r6b's (the Foundry imports and the character sheets); an example naming a `p2c` id
 * declared `SEEDED_BY_P2C` (CS r6a deliverable 188) until the `demo` template held the layer
 * (hotfix/demo-p2c, DECISIONS 811). `foundry.imports.apply` skips its success for another reason:
 * an apply reads the import's stored file under the data directory.
 */
export const EXAMPLE_ID_LAYERS = ['p1a', 'p1b', 'p2a', 'p2b', 'p2c'];
/**
 * The row's condition: `live`; a `stub` (a draft document with `is_stub`); `trashed` (`deleted_at`
 * set); `merged` (`merged_into_id` set, and trashed by the merge); `archived` (`archived_at` set);
 * or `absent`, no row at all.
 */
export const EXAMPLE_ROW_CONDITIONS = [
    'live',
    'stub',
    'trashed',
    'merged',
    'archived',
    'absent',
];
const ROWS = {
    // Documents (counter 00xx).
    questMissingLamplighters: {
        id: '01a096fd-a380-7000-8000-0000000e0001',
        kind: 'document',
        layer: 'p1a',
        state: {
            condition: 'live',
            typeKey: 'quest',
            title: 'Missing Lamplighters',
            bodyVersion: 1,
            metaVersion: 1,
        },
    },
    questSaltInTheWells: {
        id: '01a096fd-a380-7000-8000-0000000e0002',
        kind: 'document',
        layer: 'p1a',
        state: {
            condition: 'live',
            typeKey: 'quest',
            title: 'Salt in the Wells',
            bodyVersion: 2,
            metaVersion: 1,
            note: 'one document_revision: rev_no 1, reason manual, body_version 1',
        },
    },
    questSunkenChapel: {
        id: '01a096fd-a380-7000-8000-0000000e0003',
        kind: 'document',
        layer: 'p1a',
        state: {
            condition: 'live',
            typeKey: 'quest',
            title: 'The Sunken Chapel',
            bodyVersion: 1,
            metaVersion: 1,
        },
    },
    questBellsBeneathTheMarsh: {
        id: '01a096fd-a380-7000-8000-0000000e0004',
        kind: 'document',
        layer: 'p1a',
        state: {
            condition: 'live',
            typeKey: 'quest',
            title: 'Bells Beneath the Marsh',
            bodyVersion: 1,
            note: 'the source page of the docs.putBody example move, whose token names body version 1',
        },
    },
    questAbbotsBargain: {
        id: '01a096fd-a380-7000-8000-0000000e0005',
        kind: 'document',
        layer: 'p1a',
        state: { condition: 'live', typeKey: 'quest', title: "The Abbot's Bargain" },
    },
    arcRisingWater: {
        id: '01a096fd-a380-7000-8000-0000000e0006',
        kind: 'document',
        layer: 'p1a',
        state: { condition: 'live', typeKey: 'arc', title: 'The Rising Water' },
    },
    npcOrlaVenn: {
        id: '01a096fd-a380-7000-8000-0000000e0007',
        kind: 'document',
        layer: 'p1a',
        state: { condition: 'live', typeKey: 'npc', title: 'Captain Orla Venn' },
    },
    npcOldSef: {
        id: '01a096fd-a380-7000-8000-0000000e0008',
        kind: 'document',
        layer: 'p1a',
        state: {
            condition: 'live',
            typeKey: 'npc',
            title: 'Old Sef',
            note: 'named in plain text in Missing Lamplighters (an unlinked mention); holds the docs.quarantine example\'s entry: "about forty" set aside under Loyalty (fieldNpcLoyalty), in 1.B-1\'s entry shape, kind text, reason conversion',
        },
    },
    npcMaelisDorn: {
        id: '01a096fd-a380-7000-8000-0000000e0009',
        kind: 'document',
        layer: 'p1a',
        state: { condition: 'live', typeKey: 'npc', title: 'Maelis Dorn' },
    },
    npcGuardCaptainRell: {
        id: '01a096fd-a380-7000-8000-0000000e000a',
        kind: 'document',
        layer: 'p1a',
        state: {
            condition: 'stub',
            typeKey: 'npc',
            title: 'Guard-Captain Rell',
            note: 'canon status draft, is_stub true',
        },
    },
    npcDockhand39: {
        id: '01a096fd-a380-7000-8000-0000000e000b',
        kind: 'document',
        layer: 'p1a',
        state: { condition: 'live', typeKey: 'npc', title: 'Dockhand 39', metaVersion: 1 },
    },
    npcDockhand40: {
        id: '01a096fd-a380-7000-8000-0000000e000c',
        kind: 'document',
        layer: 'p1a',
        state: { condition: 'live', typeKey: 'npc', title: 'Dockhand 40' },
    },
    pcTamsinReed: {
        id: '01a096fd-a380-7000-8000-0000000e000d',
        kind: 'document',
        layer: 'p1a',
        state: { condition: 'live', typeKey: 'pc', title: 'Tamsin Reed' },
    },
    deityDrownedQueen: {
        id: '01a096fd-a380-7000-8000-0000000e000e',
        kind: 'document',
        layer: 'p1a',
        state: {
            condition: 'live',
            typeKey: 'deity',
            title: 'the Drowned Queen',
            in: 'typeDeity',
            bodyVersion: 1,
        },
    },
    itemTidebell: {
        id: '01a096fd-a380-7000-8000-0000000e000f',
        kind: 'document',
        layer: 'p1a',
        state: { condition: 'live', typeKey: 'item', title: 'the Tidebell' },
    },
    itemLanternOfWarding: {
        id: '01a096fd-a380-7000-8000-0000000e0010',
        kind: 'document',
        layer: 'p1a',
        state: { condition: 'live', typeKey: 'item', title: 'the Lantern of Warding' },
    },
    locationOldQuay: {
        id: '01a096fd-a380-7000-8000-0000000e0011',
        kind: 'document',
        layer: 'p1a',
        state: { condition: 'live', typeKey: 'location', title: 'the Old Quay' },
    },
    locationLanternQuarter: {
        id: '01a096fd-a380-7000-8000-0000000e0012',
        kind: 'document',
        layer: 'p1a',
        state: { condition: 'live', typeKey: 'location', title: 'the Lantern Quarter' },
    },
    locationSunkenChapelCrypt: {
        id: '01a096fd-a380-7000-8000-0000000e0013',
        kind: 'document',
        layer: 'p1a',
        state: { condition: 'live', typeKey: 'location', title: 'the Sunken Chapel Crypt' },
    },
    loreHistoryOfVesk: {
        id: '01a096fd-a380-7000-8000-0000000e0014',
        kind: 'document',
        layer: 'p1a',
        state: {
            condition: 'live',
            typeKey: 'lore',
            title: 'A History of Vesk',
            bodyVersion: 1,
            metaVersion: 1,
        },
    },
    noteHarbourGossip: {
        id: '01a096fd-a380-7000-8000-0000000e0015',
        kind: 'document',
        layer: 'p1a',
        state: {
            condition: 'live',
            typeKey: 'note',
            title: 'Harbour Gossip',
            metaVersion: 1,
            note: 'a small synthetic note, trashed by the docs.trash example',
        },
    },
    noteTornLedger: {
        id: '01a096fd-a380-7000-8000-0000000e0016',
        kind: 'document',
        layer: 'p1a',
        state: {
            condition: 'trashed',
            typeKey: 'note',
            title: 'A Torn Ledger',
            metaVersion: 1,
            note: 'a small synthetic note in the trash',
        },
    },
    noteLamplighterNotes: {
        id: '01a096fd-a380-7000-8000-0000000e0017',
        kind: 'document',
        layer: 'p1a',
        state: {
            condition: 'merged',
            typeKey: 'note',
            title: 'Lamplighter Notes',
            target: 'questMissingLamplighters',
            note: 'a small synthetic note merged into Missing Lamplighters',
        },
    },
    noteTideMother: {
        id: '01a096fd-a380-7000-8000-0000000e0018',
        kind: 'document',
        layer: 'p1a',
        state: {
            condition: 'live',
            typeKey: 'note',
            title: 'The Tide Mother',
            metaVersion: 1,
            note: 'a small synthetic note, retyped to Deity by the docs.retype example',
        },
    },
    docMissing: {
        id: '01a096fd-a380-7000-8000-0000000e0019',
        kind: 'document',
        layer: 'p1a',
        state: { condition: 'absent', note: 'no document has this id (docs.titles: missing)' },
    },
    docCreatedByExample: {
        id: '01a096fd-a380-7000-8000-0000000e001a',
        kind: 'document',
        layer: 'p1a',
        state: { condition: 'absent', note: 'the document the docs.create example answer names' },
    },
    loreTheFloodCreated: {
        id: '01a096fd-a380-7000-8000-0000000e001b',
        kind: 'document',
        layer: 'p1a',
        state: {
            condition: 'absent',
            note: 'the child page the docs.moveBlocks example answer names',
        },
    },
    creatureDrownedAcolyte: {
        id: '01a096fd-a380-7000-8000-0000000e001c',
        kind: 'document',
        layer: 'p1a',
        state: { condition: 'live', typeKey: 'creature', title: 'Drowned Acolyte' },
    },
    creatureBrineHag: {
        id: '01a096fd-a380-7000-8000-0000000e001d',
        kind: 'document',
        layer: 'p1a',
        state: { condition: 'live', typeKey: 'creature', title: 'Brine Hag' },
    },
    npcPaleAbbot: {
        id: '01a096fd-a380-7000-8000-0000000e001e',
        kind: 'document',
        layer: 'p1a',
        state: { condition: 'live', typeKey: 'npc', title: 'the Pale Abbot' },
    },
    sessionLampsOutOnTheQuay: {
        id: '01a096fd-a380-7000-8000-0000000e001f',
        kind: 'document',
        layer: 'p1b',
        state: {
            condition: 'live',
            typeKey: 'session',
            title: 'Lamps Out on the Quay',
            note: 'session_meta number 1, processed',
        },
    },
    sessionSaltInTheWells: {
        id: '01a096fd-a380-7000-8000-0000000e0020',
        kind: 'document',
        layer: 'p1b',
        state: {
            condition: 'live',
            typeKey: 'session',
            title: 'Salt in the Wells',
            note: 'session_meta number 2, played',
        },
    },
    sessionSunkenChapel: {
        id: '01a096fd-a380-7000-8000-0000000e0021',
        kind: 'document',
        layer: 'p1b',
        state: {
            condition: 'live',
            typeKey: 'session',
            title: 'The Sunken Chapel',
            note: 'session_meta number 3, prepped',
        },
    },
    loreCoronation: {
        id: '01a096fd-a380-7000-8000-0000000e0022',
        kind: 'document',
        layer: 'p1b',
        state: {
            condition: 'live',
            typeKey: 'lore',
            title: 'The Coronation',
            note: 'undated, before the Siege, with an alternate placement in 1038',
        },
    },
    // The SRD library's rows (CS r3a): 1.AA's ids, `srdDocumentId('<upstream key>')`.
    referenceGhoul: {
        id: '582b9817-610d-5579-a25b-2d8ea478758a',
        kind: 'document',
        layer: 'p1b',
        state: {
            condition: 'live',
            typeKey: 'ref_creature',
            title: 'Ghoul',
            note: "the SRD Ghoul's reference page",
        },
        srd: { upstreamKey: 'srd_ghoul' },
    },
    referenceSpeakWithDead: {
        id: '3c4980f6-44c4-59af-8215-5f0f757db520',
        kind: 'document',
        layer: 'p1b',
        state: {
            condition: 'live',
            typeKey: 'ref_spell',
            title: 'Speak with Dead',
            note: "the SRD spell's reference page, with its card",
        },
        srd: { upstreamKey: 'srd_speak-with-dead' },
    },
    // Blocks (counter 01xx): each sits in the body of the document `in` names.
    blockSunkenChapelOpening: {
        id: '01a096fd-a380-7000-8000-0000000e0101',
        kind: 'block',
        layer: 'p1a',
        state: { condition: 'live', in: 'questSunkenChapel', note: 'a paragraph' },
    },
    blockBellsMoved: {
        id: '01a096fd-a380-7000-8000-0000000e0102',
        kind: 'block',
        layer: 'p1a',
        state: {
            condition: 'live',
            in: 'questBellsBeneathTheMarsh',
            note: 'a paragraph; the docs.putBody example moves it into The Sunken Chapel',
        },
    },
    blockHistoryFloodHeading: {
        id: '01a096fd-a380-7000-8000-0000000e0103',
        kind: 'block',
        layer: 'p1a',
        state: { condition: 'live', in: 'loreHistoryOfVesk', note: 'a heading, "The Flood"' },
    },
    blockHistoryFloodText: {
        id: '01a096fd-a380-7000-8000-0000000e0104',
        kind: 'block',
        layer: 'p1a',
        state: { condition: 'live', in: 'loreHistoryOfVesk', note: 'the paragraph after that heading' },
    },
    blockHistoryLanternQuarter: {
        id: '01a096fd-a380-7000-8000-0000000e0105',
        kind: 'block',
        layer: 'p1a',
        state: { condition: 'live', in: 'loreHistoryOfVesk', note: 'a paragraph' },
    },
    blockOldQuayImage: {
        id: '01a096fd-a380-7000-8000-0000000e0106',
        kind: 'block',
        layer: 'p1a',
        state: {
            condition: 'live',
            in: 'locationOldQuay',
            note: 'an image block with its asset_pending row (the pending-image Inbox item)',
        },
    },
    blockMissingLamplightersOldSef: {
        id: '01a096fd-a380-7000-8000-0000000e0107',
        kind: 'block',
        layer: 'p1a',
        state: {
            condition: 'live',
            in: 'questMissingLamplighters',
            note: 'a paragraph holding the plain text "Old Sef" (occurrence 0)',
        },
    },
    blockDrownedBellOpening: {
        id: '01a096fd-a380-7000-8000-0000000e0108',
        kind: 'block',
        layer: 'p1a',
        state: {
            condition: 'absent',
            in: 'docCreatedByExample',
            note: 'the paragraph the docs.create example sends; the document it creates holds it',
        },
    },
    // Types (02xx), fields (03xx) and templates (04xx).
    typeDeity: {
        id: '01a096fd-a380-7000-8000-0000000e0201',
        kind: 'doc_type',
        layer: 'p1a',
        state: { condition: 'live', key: 'deity', title: 'Deity', note: 'capabilities: speaker' },
    },
    typeSeaBeast: {
        id: '01a096fd-a380-7000-8000-0000000e0202',
        kind: 'doc_type',
        layer: 'p1b',
        state: {
            condition: 'live',
            key: 'sea_beast',
            title: 'Sea Beast',
            note: 'a small synthetic user type with has_sheet, for the variants.create example',
        },
    },
    fieldDeityDomain: {
        id: '01a096fd-a380-7000-8000-0000000e0301',
        kind: 'field_def',
        layer: 'p1a',
        state: { condition: 'live', in: 'typeDeity', typeKey: 'deity', key: 'domain', note: 'select' },
    },
    fieldDeityHolySymbol: {
        id: '01a096fd-a380-7000-8000-0000000e0302',
        kind: 'field_def',
        layer: 'p1a',
        state: {
            condition: 'live',
            in: 'typeDeity',
            typeKey: 'deity',
            key: 'holy_symbol',
            note: 'text',
        },
    },
    fieldDeityEpithet: {
        id: '01a096fd-a380-7000-8000-0000000e0303',
        kind: 'field_def',
        layer: 'p1a',
        state: {
            condition: 'archived',
            in: 'typeDeity',
            typeKey: 'deity',
            key: 'epithet',
            note: 'a small synthetic archived text field, restored by the fields.restore example',
        },
    },
    fieldNpcLoyalty: {
        id: '01a096fd-a380-7000-8000-0000000e0304',
        kind: 'field_def',
        layer: 'p1a',
        state: { condition: 'live', typeKey: 'npc', key: 'loyalty', note: 'number' },
    },
    fieldNpcStanding: {
        id: '01a096fd-a380-7000-8000-0000000e0305',
        kind: 'field_def',
        layer: 'p1b',
        state: {
            condition: 'live',
            typeKey: 'npc',
            key: 'standing',
            title: 'Standing',
            note: 'temporal select, options Ally, Wary and Hostile: the field of the dated-state examples',
        },
    },
    fieldDeityWorshippers: {
        id: '01a096fd-a380-7000-8000-0000000e0306',
        kind: 'field_def',
        layer: 'p1a',
        state: {
            condition: 'live',
            in: 'typeDeity',
            typeKey: 'deity',
            key: 'worshippers',
            note: 'doc_refs over member_of, inbound (the demo id it replaced, DECISIONS 70 item 2)',
        },
    },
    templateDeityCustom: {
        id: '01a096fd-a380-7000-8000-0000000e0401',
        kind: 'template',
        layer: 'p1a',
        state: { condition: 'live', in: 'typeDeity', typeKey: 'deity', note: 'variant custom' },
    },
    // Links (05xx), sidebar items (06xx), assets (07xx) and exports (08xx).
    linkSaltGivenByOrla: {
        id: '01a096fd-a380-7000-8000-0000000e0501',
        kind: 'link',
        layer: 'p1a',
        state: {
            condition: 'live',
            in: 'questSaltInTheWells',
            target: 'npcOrlaVenn',
            rv: 1,
            note: 'given_by',
        },
    },
    linkTidebellRelatedToLantern: {
        id: '01a096fd-a380-7000-8000-0000000e0502',
        kind: 'link',
        layer: 'p1a',
        state: {
            condition: 'live',
            in: 'itemTidebell',
            target: 'itemLanternOfWarding',
            rv: 1,
            note: 'related_to',
        },
    },
    linkTidebellRelatedToLanternDuplicate: {
        id: '01a096fd-a380-7000-8000-0000000e0503',
        kind: 'link',
        layer: 'p1a',
        state: {
            condition: 'live',
            in: 'itemTidebell',
            target: 'itemLanternOfWarding',
            rv: 1,
            note: 'related_to, an exact duplicate of linkTidebellRelatedToLantern',
        },
    },
    linkArcContainsMissingLamplighters: {
        id: '01a096fd-a380-7000-8000-0000000e0504',
        kind: 'link',
        layer: 'p1a',
        state: {
            condition: 'live',
            in: 'arcRisingWater',
            target: 'questMissingLamplighters',
            rv: 1,
            note: 'contains, primary, first in order',
        },
    },
    linkArcContainsSaltInTheWells: {
        id: '01a096fd-a380-7000-8000-0000000e0505',
        kind: 'link',
        layer: 'p1a',
        state: {
            condition: 'live',
            in: 'arcRisingWater',
            target: 'questSaltInTheWells',
            rv: 1,
            note: 'contains, primary, second in order',
        },
    },
    sidebarPinSaltInTheWells: {
        id: '01a096fd-a380-7000-8000-0000000e0601',
        kind: 'sidebar_item',
        layer: 'p1a',
        state: { condition: 'live', target: 'questSaltInTheWells', note: 'kind document' },
    },
    sidebarGroupPrep: {
        id: '01a096fd-a380-7000-8000-0000000e0602',
        kind: 'sidebar_item',
        layer: 'p1a',
        state: { condition: 'live', title: 'Session prep', note: 'kind group' },
    },
    // CS r4b: the pin of the NPC view (`demo@p2a`), at the top level after the Session prep group.
    sidebarPinViewLoyalNpcs: {
        id: '01a096fd-a380-7000-8000-0000000e0603',
        kind: 'sidebar_item',
        layer: 'p2a',
        state: { condition: 'live', target: 'viewLoyalNpcs', note: 'kind view, no label' },
    },
    assetQuayEngraving: {
        id: '01a096fd-a380-7000-8000-0000000e0701',
        kind: 'asset',
        layer: 'p1a',
        state: {
            condition: 'absent',
            note: 'the image the assets.upload and assets.fetch example answers name; assets.file serves it',
        },
    },
    exportMarkdown: {
        id: '01a096fd-a380-7000-8000-0000000e0801',
        kind: 'export',
        layer: 'p1a',
        state: { condition: 'absent', note: 'an export job; none has run on a seeded database' },
    },
    // Quarantine entries (10xx): an entry of a document's `props_quarantine`, not a row.
    quarantineOldSefLoyalty: {
        id: '01a096fd-a380-7000-8000-0000000e1001',
        kind: 'quarantine_entry',
        layer: 'p1a',
        state: {
            condition: 'live',
            in: 'npcOldSef',
            target: 'fieldNpcLoyalty',
            note: 'Old Sef\'s "about forty", set aside under Loyalty; the docs.quarantine example\'s entry (CS r2c, DECISIONS 138 item 2)',
        },
    },
    // Chronology (09xx to 0cxx), states (0dxx), knowledge (0exx) and sheets (0fxx), all p1b.
    calendarVeskReckoning: {
        id: '01a096fd-a380-7000-8000-0000000e0901',
        kind: 'calendar',
        layer: 'p1b',
        state: { condition: 'live', title: 'Vesk Reckoning', version: 1 },
    },
    placementSaltInTheWells: {
        id: '01a096fd-a380-7000-8000-0000000e0a01',
        kind: 'placement',
        layer: 'p1b',
        state: {
            condition: 'live',
            in: 'questSaltInTheWells',
            rv: 1,
            note: 'from 9 to 10 Ember 1042 AL',
        },
    },
    placementMissingLamplighters: {
        id: '01a096fd-a380-7000-8000-0000000e0a05',
        kind: 'placement',
        layer: 'p1b',
        state: {
            condition: 'live',
            in: 'questMissingLamplighters',
            rv: 1,
            note: 'the first quest, dated',
        },
    },
    placementSunkenChapel: {
        id: '01a096fd-a380-7000-8000-0000000e0a02',
        kind: 'placement',
        layer: 'p1b',
        state: { condition: 'live', in: 'questSunkenChapel', note: 'undated, constrained after Bells' },
    },
    placementAbbotsBargain: {
        id: '01a096fd-a380-7000-8000-0000000e0a03',
        kind: 'placement',
        layer: 'p1b',
        state: { condition: 'live', in: 'questAbbotsBargain', note: 'undated and unconstrained' },
    },
    placementCoronationAlternate: {
        id: '01a096fd-a380-7000-8000-0000000e0a04',
        kind: 'placement',
        layer: 'p1b',
        state: { condition: 'live', in: 'loreCoronation', rv: 1, note: 'status alternate, in 1038 AL' },
    },
    constraintSunkenChapelAfterBells: {
        id: '01a096fd-a380-7000-8000-0000000e0b01',
        kind: 'constraint',
        layer: 'p1b',
        state: {
            condition: 'live',
            in: 'questSunkenChapel',
            rv: 1,
            note: "Bells Beneath the Marsh's placement before placementSunkenChapel",
        },
    },
    clockAdvanceToUndo: {
        id: '01a096fd-a380-7000-8000-0000000e0c01',
        kind: 'clock_advance',
        layer: 'p2b',
        state: {
            condition: 'live',
            in: 'sessionSaltInTheWells',
            note: "CS r5b: the two-hour advance session 2's promoted time capture made; NOW's only advance",
        },
    },
    stateOldSefStanding: {
        id: '01a096fd-a380-7000-8000-0000000e0d01',
        kind: 'entity_state',
        layer: 'p1b',
        state: {
            condition: 'live',
            in: 'npcOldSef',
            target: 'fieldNpcStanding',
            rv: 1,
            note: 'Old Sef\'s Standing, "Wary", from 9 Ember 1042 AL with no end',
        },
    },
    knowledgeS3Tentative: {
        id: '01a096fd-a380-7000-8000-0000000e0e01',
        kind: 'knowledge_event',
        layer: 'p1b',
        state: { condition: 'live', note: 'S3, a tentative known in session 2' },
    },
    sheetDrownedAcolyte: {
        id: '01a096fd-a380-7000-8000-0000000e0f01',
        kind: 'actor_sheet',
        layer: 'p1b',
        state: {
            condition: 'live',
            in: 'creatureDrownedAcolyte',
            version: 1,
            note: 'the primary sheet, source form',
        },
    },
    // CS r3a: 1.AA's `srdSheetId('srd_ghoul')`.
    sheetReferenceGhoul: {
        id: '09a165a6-8288-5f92-b0a1-1f01f9b193bb',
        kind: 'actor_sheet',
        layer: 'p1b',
        state: {
            condition: 'live',
            in: 'referenceGhoul',
            note: "the SRD Ghoul's primary sheet, source srd",
        },
        srd: { upstreamKey: 'srd_ghoul' },
    },
    sheetPaleAbbot: {
        id: '01a096fd-a380-7000-8000-0000000e0f02',
        kind: 'actor_sheet',
        layer: 'p1b',
        state: {
            condition: 'live',
            in: 'npcPaleAbbot',
            version: 1,
            note: 'the primary sheet, source form',
        },
    },
    // CS r6a (deliverable 188), p2c: CS r6b's imports of P0-7's and 2.G s1's synthetic files.
    sheetTamsinReed: {
        id: '01a096fd-a380-7000-8000-0000000e0f03',
        kind: 'actor_sheet',
        layer: 'p2c',
        state: {
            condition: 'live',
            in: 'pcTamsinReed',
            version: 2,
            note: 'the primary character sheet, source foundry, applied by actorImportTamsinApplied (version 1), then hp.max edited (origin local, version 2; CS r6b)',
        },
    },
    assetFoundryPartyFile: {
        id: '01a096fd-a380-7000-8000-0000000e0702',
        kind: 'asset',
        layer: 'p2c',
        state: {
            condition: 'live',
            note: "a raw asset: 2.G s1's synthetic party macro file, by hash and size",
        },
    },
    actorImportTamsinApplied: {
        id: '01a096fd-a380-7000-8000-0000000e1601',
        kind: 'actor_import',
        layer: 'p2c',
        state: {
            condition: 'live',
            in: 'assetFoundryPartyFile',
            target: 'sheetTamsinReed',
            rv: 2,
            note: "entry 0, applied to Tamsin Reed's p1a page: the newest applied import of the sheet; Undo refuses it changed_since (her hp.max edited after it)",
        },
    },
    // CS r6b: the applied import Undo's example names (Brother Hale's sheet is unedited since).
    actorImportHaleApplied: {
        id: '01a096fd-a380-7000-8000-0000000e1604',
        kind: 'actor_import',
        layer: 'p2c',
        state: {
            condition: 'live',
            in: 'assetFoundryPartyFile',
            rv: 2,
            note: "entry 1, applied to Brother Hale's p1a page: the newest applied import of an unedited sheet, which Undo may undo",
        },
    },
    actorImportAcolytePreviewed: {
        id: '01a096fd-a380-7000-8000-0000000e1602',
        kind: 'actor_import',
        layer: 'p2c',
        state: {
            condition: 'live',
            in: 'assetFoundryPartyFile',
            rv: 1,
            note: "entry 4, the Drowned Acolyte (npc), previewed: identity proposed (name_confirmed, the p1b Acolyte's page), no compendium",
        },
    },
    batchTamsinImport: {
        id: '01a096fd-a380-7000-8000-0000000e1603',
        kind: 'change_batch',
        layer: 'p2c',
        state: {
            condition: 'live',
            note: 'the apply of actorImportTamsinApplied (kind foundry.imports.apply, origin import)',
        },
    },
    // CS r6c (deliverable 202), p2c: CS r6b's homebrew spell page, with its card (rules.update).
    spellBrineWard: {
        id: '01a096fd-a380-7000-8000-0000000e0023',
        kind: 'document',
        layer: 'p2c',
        state: {
            condition: 'live',
            typeKey: 'spell',
            title: 'Brine Ward',
            rv: 1,
            note: "a canon spell page with its card (rules_record kind spell, source form): rv is the card's",
        },
    },
    // CS r6b (DECISIONS 800.4), p2c: 2.M's card variant of the SRD's Speak with Dead.
    spellSpeakWithTheDrowned: {
        id: '01a096fd-a380-7000-8000-0000000e0024',
        kind: 'document',
        layer: 'p2c',
        state: {
            condition: 'live',
            typeKey: 'spell',
            title: 'Speak with the Drowned',
            bodyVersion: 1,
            metaVersion: 1,
            target: 'referenceSpeakWithDead',
            note: 'a card variant (variant_of the reference spell, card source srd_copy, its body one ruleCardEmbed); absent without the library',
        },
    },
    // Import runs (11xx), p2a: CS r4b's demo import of 2.A's synthetic export.
    importRunPlanned: {
        id: '01a096fd-a380-7000-8000-0000000e1101',
        kind: 'import_run',
        layer: 'p2a',
        state: {
            condition: 'live',
            rv: 2,
            note: 'a planned run of the synthetic HTML export, its mapping proposed, nothing applied',
        },
    },
    importRunUploading: {
        id: '01a096fd-a380-7000-8000-0000000e1103',
        kind: 'import_run',
        layer: 'p2a',
        state: {
            condition: 'live',
            rv: 2,
            note: 'an upload of 20 MiB in three parts, the first received',
        },
    },
    importRunCreated: {
        id: '01a096fd-a380-7000-8000-0000000e1104',
        kind: 'import_run',
        layer: 'p2a',
        state: { condition: 'absent', note: 'the run the import.runs.create example answer names' },
    },
    importRunApplied: {
        id: '01a096fd-a380-7000-8000-0000000e1102',
        kind: 'import_run',
        layer: 'p2a',
        state: {
            condition: 'live',
            rv: 5,
            note: 'the newest applied run, which Undo import may undo',
        },
    },
    // Run-sheet items (12xx) and session captures (13xx), p2b: CS r5b's demo of session 3.
    sessionItemFloodScene: {
        id: '01a096fd-a380-7000-8000-0000000e1201',
        kind: 'session_item',
        layer: 'p2b',
        state: {
            condition: 'live',
            in: 'sessionSunkenChapel',
            target: 'blockHistoryFloodHeading',
            rv: 1,
            note: 'a scene item of session 3: the heading "The Flood" of A History of Vesk, first on the sheet',
        },
    },
    sessionItemChapelNote: {
        id: '01a096fd-a380-7000-8000-0000000e1202',
        kind: 'session_item',
        layer: 'p2b',
        state: {
            condition: 'live',
            in: 'sessionSunkenChapel',
            rv: 1,
            note: 'a note item of session 3, after the scene',
        },
    },
    captureChapelLoot: {
        id: '01a096fd-a380-7000-8000-0000000e1301',
        kind: 'capture',
        layer: 'p2b',
        state: {
            condition: 'live',
            in: 'sessionSunkenChapel',
            rv: 1,
            note: 'a session capture of kind loot, seq 1, open',
        },
    },
    // Session 2's captures of demo@p2b (13xx, CS r5b deliverable 167).
    captureSaltStub: {
        id: '01a096fd-a380-7000-8000-0000000e1302',
        kind: 'capture',
        layer: 'p2b',
        state: {
            condition: 'live',
            in: 'sessionSaltInTheWells',
            rv: 1,
            note: 'an open stub capture of session 2',
        },
    },
    captureSaltTime: {
        id: '01a096fd-a380-7000-8000-0000000e1303',
        kind: 'capture',
        layer: 'p2b',
        state: {
            condition: 'live',
            in: 'sessionSaltInTheWells',
            rv: 2,
            note: 'a time capture of session 2, promoted: clockAdvanceToUndo names it',
        },
    },
    // Session 2's sources, the reveal, the before-tracking event and the batch (15xx), p2b.
    sourceSaltSummary: {
        id: '01a096fd-a380-7000-8000-0000000e1501',
        kind: 'session_source',
        layer: 'p2b',
        state: {
            condition: 'live',
            in: 'sessionSaltInTheWells',
            rv: 1,
            note: "a gma_summary source: P0-13's synthetic summary file, by hash and size",
        },
    },
    sourceSaltTranscript: {
        id: '01a096fd-a380-7000-8000-0000000e1502',
        kind: 'session_source',
        layer: 'p2b',
        state: {
            condition: 'live',
            in: 'sessionSaltInTheWells',
            rv: 1,
            note: "a transcript source: P0-13's synthetic WebVTT file, by hash and size",
        },
    },
    knowledgeS1Tentative: {
        id: '01a096fd-a380-7000-8000-0000000e1503',
        kind: 'knowledge_event',
        layer: 'p2b',
        state: { condition: 'live', note: 'S1, a second tentative known in session 2, beside S3' },
    },
    knowledgeFloodBeforeTracking: {
        id: '01a096fd-a380-7000-8000-0000000e1504',
        kind: 'knowledge_event',
        layer: 'p2b',
        state: {
            condition: 'live',
            target: 'loreHistoryOfVesk',
            note: 'known before tracking began, origin import, on the document of the Flood placement',
        },
    },
    batchSaltTimePromote: {
        id: '01a096fd-a380-7000-8000-0000000e1505',
        kind: 'change_batch',
        layer: 'p2b',
        state: {
            condition: 'live',
            in: 'sessionSaltInTheWells',
            note: "the promote of captureSaltTime: its change_log rows are the capture's update and the advance",
        },
    },
    // Saved views (14xx), p2a: CS r4b's two views (deliverable 123).
    viewLoyalNpcs: {
        id: '01a096fd-a380-7000-8000-0000000e1401',
        kind: 'saved_view',
        layer: 'p2a',
        state: {
            condition: 'live',
            typeKey: 'npc',
            title: 'Loyal NPCs',
            rv: 1,
            note: 'a table filtered on loyalty, pinned by sidebarPinViewLoyalNpcs',
        },
    },
    viewQuestBoard: {
        id: '01a096fd-a380-7000-8000-0000000e1402',
        kind: 'saved_view',
        layer: 'p2a',
        state: {
            condition: 'live',
            typeKey: 'quest',
            title: 'Quest board',
            rv: 1,
            note: 'a list, not pinned',
        },
    },
};
for (const row of Object.values(ROWS)) {
    Object.freeze(row.state);
    if ('srd' in row)
        Object.freeze(row.srd);
    Object.freeze(row);
}
Object.freeze(ROWS);
/** Every example id by name. */
export const EXAMPLE_IDS = ROWS;
const VALUES = new Set(Object.values(ROWS).map((row) => row.id));
/** True for a value of {@link EXAMPLE_IDS}. */
export function isExampleId(value) {
    return typeof value === 'string' && VALUES.has(value);
}
//# sourceMappingURL=example-ids.js.map